import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(enLocale);

/** ISO 3166-1 alpha-3 countries (the KYB's country fields), sorted by English name. */
export const ALPHA_3_COUNTRIES: { value: string; label: string }[] = Object.keys(countries.getAlpha3Codes())
  .map((code) => ({ value: code, label: `${countries.getName(code, 'en') ?? code} (${code})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

/**
 * The company KYB's Alpaca values — the full lists from Alpaca's Business Trading Accounts docs
 * (https://docs.alpaca.markets/us/docs/business-trading-accounts). ("llc" is NOT a legal entity type value.)
 */
export const TREASURY_KYB_LEGAL_ENTITY_TYPES: { value: string; label: string }[] = [
  { value: 'limited_liability_corporation', label: 'Limited Liability Corporation (LLC)' },
  { value: 'c_corporation', label: 'C Corporation' },
  { value: 's_corporation', label: 'S Corporation' },
  { value: 'general_partnership', label: 'General Partnership' },
  { value: 'limited_partnership', label: 'Limited Partnership' },
  { value: 'banking_institution', label: 'Banking Institution' },
  { value: 'broker_or_dealer', label: 'Broker or Dealer' },
  { value: 'foreign_financial_institution', label: 'Foreign Financial Institution' },
  { value: 'fund_or_hedge_fund', label: 'Fund or Hedge Fund' },
  { value: 'irrevocable_trust', label: 'Irrevocable Trust' },
  { value: 'revocable_trust', label: 'Revocable Trust' },
  { value: 'unincorporated', label: 'Unincorporated' },
  { value: 'other', label: 'Other' },
];

export const TREASURY_KYB_INDUSTRIES: { value: string; label: string }[] = [
  { value: 'finance', label: 'Finance' },
  { value: 'administration_or_public_relations', label: 'Administration / Public Relations' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'architecture', label: 'Architecture' },
  { value: 'arts_or_film_or_music', label: 'Arts / Film / Music' },
  { value: 'construction', label: 'Construction' },
  { value: 'design', label: 'Design' },
  { value: 'education', label: 'Education' },
  { value: 'food_production', label: 'Food Production' },
  { value: 'government_or_public_services', label: 'Government / Public Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'legal_services', label: 'Legal Services' },
  { value: 'media_or_marketing', label: 'Media / Marketing' },
  { value: 'oil_or_energy_or_mining', label: 'Oil / Energy / Mining' },
  { value: 'pharmaceuticals_or_biochemicals', label: 'Pharmaceuticals / Biochemicals' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'retail_restaurants', label: 'Retail / Restaurants' },
  { value: 'technology', label: 'Technology' },
  { value: 'sports_or_sports_medicine_or_fitness', label: 'Sports / Sports Medicine / Fitness' },
  { value: 'tourism', label: 'Tourism' },
  { value: 'transport', label: 'Transport' },
  { value: 'other', label: 'Other' },
];

export const TREASURY_KYB_FUNDING_SOURCES: { value: string; label: string }[] = [
  { value: 'business_revenue', label: 'Business Revenue' },
  { value: 'asset_appreciation', label: 'Asset Appreciation' },
  { value: 'sales_of_assets', label: 'Sales of Assets' },
  { value: 'investors', label: 'Investors' },
  { value: 'other', label: 'Other' },
];

export const TREASURY_KYB_DEFAULT_LEGAL_ENTITY_TYPE = 'limited_liability_corporation';
export const TREASURY_KYB_DEFAULT_INDUSTRY = 'finance';
export const TREASURY_KYB_DEFAULT_FUNDING_SOURCES = ['business_revenue'];

/** The KYB owner's share — the backend supports exactly one 100% owner. */
export const TREASURY_KYB_OWNER_PERCENTAGE = 100;

/** The treasury onboarding steps, in the order the backend enforces them. */
export enum TreasuryStep {
  GroupAndDepartment = 0,
  TreasuryEmployee = 1,
  PersonalEmail = 2,
  ApproveEmployee = 3,
  ConfirmKyc = 4,
  CompanyKyb = 5,
  AdminVerification = 6,
  AlpacaAgreements = 7,
  AlpacaAccount = 8,
}

export const TREASURY_STEP_LABELS: Record<TreasuryStep, string> = {
  [TreasuryStep.GroupAndDepartment]: 'Group & Department',
  [TreasuryStep.TreasuryEmployee]: 'Treasury Employee',
  [TreasuryStep.PersonalEmail]: 'Personal Email',
  [TreasuryStep.ApproveEmployee]: 'Approve Employee',
  [TreasuryStep.ConfirmKyc]: 'Confirm KYC',
  [TreasuryStep.CompanyKyb]: 'Company KYB',
  [TreasuryStep.AdminVerification]: 'Admin Verification',
  [TreasuryStep.AlpacaAgreements]: 'Alpaca Agreements',
  [TreasuryStep.AlpacaAccount]: 'Alpaca Account',
};

/** One line under each step's title — what the step is for. */
export const TREASURY_STEP_DESCRIPTIONS: Record<TreasuryStep, string> = {
  [TreasuryStep.GroupAndDepartment]: 'Create the treasury bank, group (with its one product) and department.',
  [TreasuryStep.TreasuryEmployee]: 'Onboard the company owner — the one treasury employee.',
  [TreasuryStep.PersonalEmail]: "Replace the owner's work email with their personal email.",
  [TreasuryStep.ApproveEmployee]: 'Approve the owner — this makes the treasury active.',
  [TreasuryStep.ConfirmKyc]: "Confirm the owner's KYC — the owner's account can't turn active without it.",
  [TreasuryStep.CompanyKyb]: "What Alpaca needs about the company, and the owner's address.",
  [TreasuryStep.AdminVerification]: 'Confirm the company was checked by hand.',
  [TreasuryStep.AlpacaAgreements]: "The owner's account opening agreement and the company's Alpaca agreements.",
  [TreasuryStep.AlpacaAccount]: "Follow the company's Alpaca account until it opens.",
};

/**
 * The treasury group's `contribution`. Money never reads it — a treasury contribution is always 0% employee / 100%
 * company — but the backend requires it (NOT NULL), so it is sent as 100 to match what is shown.
 */
export const TREASURY_GROUP_CONTRIBUTION = 100;

/** The owner's funding sources, as Alpaca's person party takes them — madkhol-admin TREASURY_OWNER_FUNDING_SOURCES. */
export const TREASURY_OWNER_FUNDING_SOURCES: { value: string; label: string }[] = [
  { value: 'employment_income', label: 'Employment Income' },
  { value: 'business_income', label: 'Business Income' },
  { value: 'savings', label: 'Savings' },
  { value: 'investments', label: 'Investments' },
  { value: 'inheritance', label: 'Inheritance' },
  { value: 'family', label: 'Family' },
];

/**
 * The four documents the company Alpaca account needs, uploaded as company documents (thriftplan document types
 * registration, formation, articles, owner_id). `missingCode` is the readiness code while one is missing.
 */
export const TREASURY_KYB_DOCUMENTS: { type: string; label: string; missingCode: string }[] = [
  { type: 'registration', label: 'CR Certificate', missingCode: 'DOCUMENT_MISSING_REGISTRATION' },
  { type: 'formation', label: 'Certificate of Formation', missingCode: 'DOCUMENT_MISSING_FORMATION' },
  { type: 'articles', label: 'Articles of Association', missingCode: 'DOCUMENT_MISSING_ARTICLES' },
  { type: 'owner_id', label: "Owner's ID Scan", missingCode: 'DOCUMENT_MISSING_OWNER_ID' },
];

/** The six company checks "company verified" sends to Alpaca as the admin's manual review. */
export const TREASURY_VERIFICATION_CHECKS: string[] = [
  'Business registration',
  'Business status',
  'Ownership',
  'Address',
  'Watchlist',
  'Identification',
];

/**
 * The owner's own agreement — the same three checkboxes as the mobile app's "Sign Agreement" screen
 * (madkhol-mobile OpeningAccountAgreement), with its English text. All three must be ticked before signing.
 */
export const TREASURY_OWNER_AGREEMENT_CHECKS: { key: string; label: string }[] = [
  { key: 'agreeAccountOpening', label: 'Agree the Account Opening Agreement' },
  { key: 'agreeAlpaca', label: 'Agree the Alpaca Customer Agreement' },
  {
    key: 'notUSCitizen',
    label:
      'I certify that I am not a US citizen, US resident alien or other US person for US tax purposes. I am submitting the applicable Form W-8 BEN',
  },
];

/** The two Alpaca agreements accepted in step 7 (Alpaca Business Trading Accounts docs). */
export const TREASURY_ALPACA_AGREEMENTS: { name: string; label: string; description: string }[] = [
  {
    name: 'certifications_and_resolutions_statement',
    label: 'Certifications and Resolutions Statement',
    description: 'Company level — signed for the company by its authorized signer, the owner.',
  },
  {
    name: 'customer_agreement',
    label: 'Customer Agreement',
    description: "Account level — Alpaca's standard brokerage customer agreement for the company account.",
  },
];

/**
 * Every readiness code (thriftplan KybOnboardingMissing) in plain words, with what fixes it. An unknown code is shown
 * as-is.
 */
export const TREASURY_READINESS_LABELS: Record<string, string> = {
  TREASURY_NOT_SET_UP: 'The company has no treasury setup.',
  TREASURY_NOT_ACTIVE: 'The treasury employee is not approved yet.',
  COMPANY_NOT_ACTIVE: 'The company is not active (or is deleted).',
  COMPANY_PRIMARY_ADDRESS_MISSING: 'The company has no primary address — add one on the company.',
  KYB_MISSING: 'The company KYB is not saved.',
  PARTY_EMAILS_MISSING: 'The KYB has no Alpaca party emails — save the KYB again.',
  OWNER_NOT_TREASURY_EMPLOYEE: 'The KYB owner is not the current treasury employee — save the KYB again.',
  OWNER_USER_MISSING: "The owner's Madkhol user is missing.",
  OWNER_KYC_MISSING: "The owner's KYC is missing.",
  OWNER_FUNDING_SOURCE_MISSING: "The owner's funding source is missing.",
  OWNER_COUNTRY_UNKNOWN: "The owner's nationality or country could not be matched to a country code.",
  OWNER_B2C_KYC_UNAVAILABLE: "The owner's personal KYC could not be read — try again later.",
  PARTY_EMAIL_IN_USE:
    "A party email is already used (by the other one, or by the owner's own emails) — change it on the KYB.",
  OWNER_MANUAL_CHECK_REQUIRED:
    'The owner is flagged "Manual Check Required" by World-Check — compliance must clear them.',
  OWNER_WORLD_CHECK_MISSING: 'The owner has no World-Check screen yet — press Retry World-Check.',
  OWNER_USER_NOT_ACTIVE:
    "The owner's Madkhol account is not Active — sign their account opening agreement (Alpaca Agreements step); if it stays Pending, activate the user from their user page.",
  USER_SIGN_ACCOUNT_OPENING_AGREEMENT_NOT_FOUND:
    "The owner's account opening agreement is not signed — sign it on the Alpaca Agreements step (or the owner signs in the app).",
  KYB_NOT_VERIFIED: 'The company is not verified — confirm it on Admin Verification.',
  OWNER_DOCUMENT_NOT_CHECKED: "The owner's ID document tick is missing — verify again.",
  AGREEMENTS_NOT_ACCEPTED: 'The Alpaca agreements are not accepted.',
  AGREEMENTS_IP_MISSING: 'The agreements were accepted without an IP — accept them again.',
  DOCUMENT_MISSING_REGISTRATION: 'The CR certificate is not uploaded (type "registration").',
  DOCUMENT_MISSING_FORMATION: 'The certificate of formation is not uploaded (type "formation").',
  DOCUMENT_MISSING_ARTICLES: 'The articles of association are not uploaded (type "articles").',
  DOCUMENT_MISSING_OWNER_ID: 'The owner\'s ID scan is not uploaded (type "owner_id").',
};

/** Account statuses the retry starts over from — a new attempt and a new account (thriftplan RESUBMITTABLE_ACCOUNT_STATUSES). */
export const TREASURY_RESUBMITTABLE_ACCOUNT_STATUSES = ['REJECTED', 'SUBMISSION_FAILED'];

/** How long the Alpaca Account step follows a retry, waiting for the attempt to change, before it gives up (ms). */
export const TREASURY_RETRY_WATCH_MS = 60000;

/** How often the treasury employee step re-reads its onboarding row while it is queued or processing (ms). */
export const TREASURY_ONBOARDING_POLL_MS = 5000;

/** The message thriftplan sends when a company has no treasury setup. */
export const TREASURY_NOT_SET_UP = 'TREASURY_NOT_SET_UP';

/** The company Alpaca account status money can move on (thriftplan assertTreasuryCanMoveMoney). */
export const TREASURY_ACCOUNT_ACTIVE_STATUS = 'ACTIVE';

/** The company bank a test top-up (dummy statement) credits. */
export const TREASURY_TEST_TOP_UP_BANK_TYPE = 'VIRTUAL_TREASURY';

/** The owner type of the company's treasury portfolio in madkhol-portfolio (id 3). */
export const TREASURY_PORTFOLIO_OWNER_TYPE = 'employee_treasury';

/** The owner type of an employee's (B2B) portfolio in madkhol-portfolio. */
export const B2B_PORTFOLIO_OWNER_TYPE = 'employee';

/** The segment tag shown on request rows, from the subscribed portfolio's owner type. */
export enum OwnerTypeTag {
  B2B = 'B2B',
  B2C = 'B2C',
  TREASURY = 'Treasury',
}

/** The chip colour of each owner type tag. */
export const OWNER_TYPE_TAG_COLORS: Record<OwnerTypeTag, 'primary' | 'secondary' | 'default'> = {
  [OwnerTypeTag.B2B]: 'primary',
  [OwnerTypeTag.B2C]: 'default',
  [OwnerTypeTag.TREASURY]: 'secondary',
};

/** Rows per page on the treasury operation lists. */
export const TREASURY_LIST_PAGE_SIZE = 10;

/** Height of a treasury list grid: one page of rows plus the toolbar, header and footer. */
export const TREASURY_GRID_HEIGHT = 680;

/** The first page of a treasury list — where a new row shows (the lists are newest first). */
export const TREASURY_FIRST_PAGE = { page: 0, pageSize: TREASURY_LIST_PAGE_SIZE };

/** The operation tabs, in order. */
export enum TreasuryOperationTab {
  Contributions = 'contributions',
  Redemptions = 'redemptions',
  RedemptionRules = 'redemption-rules',
  Withdrawals = 'withdrawals',
}

export const TREASURY_OPERATION_TAB_LABELS: Record<TreasuryOperationTab, string> = {
  [TreasuryOperationTab.Contributions]: 'Contributions',
  [TreasuryOperationTab.Redemptions]: 'Redemptions',
  [TreasuryOperationTab.RedemptionRules]: 'Redemption Rules',
  [TreasuryOperationTab.Withdrawals]: 'Withdrawals',
};

/** thriftplan CompanyContributionStatusEnum → chip colour. Only `pending` can be executed. */
export const TREASURY_CONTRIBUTION_PENDING = 'pending';

export const TREASURY_CONTRIBUTION_STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  pending: 'warning',
  in_progress: 'info',
  partial_done: 'info',
  done: 'success',
};

/** thriftplan TreasuryRedeemFrequency. */
export const TREASURY_REDEEM_FREQUENCIES: { value: string; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'YEARLY', label: 'Yearly' },
];

export const TREASURY_REDEEM_YEARLY = 'YEARLY';

/** thriftplan TreasuryInsufficientFundsAction — what a firing does when the portfolio is worth less than the rule. */
export const TREASURY_INSUFFICIENT_FUNDS_ACTIONS: { value: string; label: string; description: string }[] = [
  {
    value: 'REDEEM_AVAILABLE',
    label: 'Redeem what is there',
    description: 'A FULL redemption of the portfolio.',
  },
  {
    value: 'SKIP_AND_NOTIFY',
    label: 'Skip and notify',
    description: 'Nothing is redeemed this period; the company HR gets an email.',
  },
];

/** thriftplan day_of_month is 1–28 so every month has the day. */
export const TREASURY_REDEEM_MAX_DAY = 28;

export const TREASURY_MONTHS: { value: number; label: string }[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((label, index) => ({ value: index + 1, label }));

/** thriftplan TreasuryRedeemRunStatus → chip colour. */
export const TREASURY_RUN_STATUS_COLORS: Record<string, 'default' | 'error' | 'success' | 'warning'> = {
  SUCCESS: 'success',
  FAILED: 'error',
  SKIPPED_NO_FUNDS: 'warning',
  SKIPPED_NOT_REDEEMABLE: 'default',
};

/** thriftplan CompanyWithdrawStatusEnum — only `pending` can be approved or rejected. */
export const TREASURY_WITHDRAW_PENDING = 'pending';

export const TREASURY_WITHDRAW_STATUS_COLORS: Record<string, 'default' | 'error' | 'success' | 'warning'> = {
  pending: 'warning',
  approve: 'success',
  rejected: 'error',
};
